import React, { lazy, Suspense } from "react";
import async from "./components/Async";
import { fetchApi } from "@/utils/fetchApi";

// All pages that rely on 3rd party components (other than MUI) are
// loaded asynchronously, to keep the initial JS bundle to a minimum size

// Layouts
import AuthLayout from "./layouts/Auth";
import DashboardLayout from "./layouts/Dashboard";
import DocLayout from "./layouts/Doc";
import PresentationLayout from "./layouts/Presentation";

// Guards
import AuthGuard from "./components/guards/AuthGuard";
import Page404 from "./pages/auth/Page404";

// Icon components
import MaterialIcons from "./pages/icons/MaterialIcons";

// Landing
import Landing from "./pages/presentation/Landing";
import PresentationFeatures from "./pages/presentation/Features";
import PresentationPricing from "./pages/presentation/Pricing";
import PresentationContact from "./pages/presentation/Contact";
import PresentationAbout from "./pages/presentation/About";
import Cart from "./pages/presentation/Cart";

/*
// Form components
import SelectionCtrls from "./pages/forms/SelectionControls";
import Selects from "./pages/forms/Selects";
import TextFields from "./pages/forms/TextFields";

// Page components
import Blank from "./pages/pages/Blank";
import InvoiceDetails from "./pages/pages/InvoiceDetails";
import InvoiceList from "./pages/pages/InvoiceList";
import Orders from "./pages/pages/Orders";
import Pricing from "./pages/pages/Pricing";
import Settings from "./pages/pages/Settings";
import Projects from "./pages/pages/Projects";
import Chat from "./pages/pages/Chat";
import APIExamples from "./pages/pages/APIExamples";

// Table components
import SimpleTable from "./pages/tables/SimpleTable";
import AdvancedTable from "./pages/tables/AdvancedTable";
*/

// Documentation
import Welcome from "./pages/docs/Welcome";
import GettingStarted from "./pages/docs/GettingStarted";
import Routing from "./pages/docs/Routing";
import Auth0 from "./pages/docs/auth/Auth0";
import Cognito from "./pages/docs/auth/Cognito";
import Firebase from "./pages/docs/auth/Firebase";
import JWT from "./pages/docs/auth/JWT";
import Guards from "./pages/docs/Guards";
import EnvironmentVariables from "./pages/docs/EnvironmentVariables";
import Deployment from "./pages/docs/Deployment";
import Theming from "./pages/docs/Theming";
import APICalls from "./pages/docs/APICalls";
import Redux from "./pages/docs/Redux";
import Internationalization from "./pages/docs/Internationalization";
import ESLintAndPrettier from "./pages/docs/ESLintAndPrettier";
import Support from "./pages/docs/Support";
import Changelog from "./pages/docs/Changelog";

import PoliciesAndProcedures from "./pages/docs/PoliciesAndProcedures";
import PoliciesAndProcedures_NoticeOfPrivacyPractices from "./pages/docs/PoliciesAndProcedures_NoticeOfPrivacyPractices";
import PoliciesAndProcedures_NoticeOfPatientRightsPolicy from "./pages/docs/PoliciesAndProcedures_NoticeOfPatientRightsPolicy";
import PoliciesAndProcedures_UseAndDisclosureOfPHI from "./pages/docs/PoliciesAndProcedures_UseAndDisclosureOfPHI";
import PoliciesAndProcedures_MinimumNecessaryUseAndDisclosure from "./pages/docs/PoliciesAndProcedures_MinimumNecessaryUseAndDisclosure";
import PoliciesAndProcedures_AuthorizationRequirements from "./pages/docs/PoliciesAndProcedures_AuthorizationRequirements";
import PoliciesAndProcedures_AuthorizationRequirements_AuthorizationForm from "./pages/docs/PoliciesAndProcedures_AuthorizationRequirements_AuthorizationForm";
import PoliciesAndProcedures_AuthorizationRequirements_AuthorizationExceptions from "./pages/docs/PoliciesAndProcedures_AuthorizationRequirements_AuthorizationExceptions";
import PoliciesAndProcedures_AuthorizationRequirements_AuthorizationRevocation from "./pages/docs/PoliciesAndProcedures_AuthorizationRequirements_AuthorizationRevocation";
import PoliciesAndProcedures_BusinessAssociateAgreements from "./pages/docs/PoliciesAndProcedures_BusinessAssociateAgreements";
import PoliciesAndProcedures_PrivacyTrainingAndAwareness from "./pages/docs/PoliciesAndProcedures_PrivacyTrainingAndAwareness";
import PoliciesAndProcedures_PrivacyComplaintsProcess from "./pages/docs/PoliciesAndProcedures_PrivacyComplaintsProcess";
import PoliciesAndProcedures_PrivacyBreachNotification from "./pages/docs/PoliciesAndProcedures_PrivacyBreachNotification";
import PoliciesAndProcedures_SanctionPolicyForPrivacyViolations from "./pages/docs/PoliciesAndProcedures_SanctionPolicyForPrivacyViolations";
import PoliciesAndProcedures_PrivacyMonitoringAndAuditing from "./pages/docs/PoliciesAndProcedures_PrivacyMonitoringAndAuditing";

import TechnicalSafeguards from "./pages/docs/TechnicalSafeguards";
import PhysicalSafeguards from "./pages/docs/PhysicalSafeguards";
import PrivacyCompliance from "./pages/docs/PrivacyCompliance";
import BreachNotification from "./pages/docs/BreachNotification";
import RecordKeeping from "./pages/docs/RecordKeeping";
import IncidentResponse from "./pages/docs/IncidentResponse";
const FeatherIcons = async(() => import("./pages/icons/FeatherIcons"));
/*
// Protected routes
import ProtectedPage from "./pages/protected/ProtectedPage";
import { layouts } from "chart.js";

// Form components
const Pickers = async(() => import("./pages/forms/Pickers"));
const Editors = async(() => import("./pages/forms/Editors"));
const Formik = async(() => import("./pages/forms/Formik"));

// Icon components
const Profile = async(() => import("./pages/pages/Profile"));
const Tasks = async(() => import("./pages/pages/Tasks"));
const Calendar = async(() => import("./pages/pages/Calendar"));

// Table components
const DataGrid = async(() => import("./pages/tables/DataGrid"));

// Chart components
const Chartjs = async(() => import("./pages/charts/Chartjs"));
const ApexCharts = async(() => import("./pages/charts/ApexCharts"));

// Maps components
const GoogleMaps = async(() => import("./pages/maps/GoogleMaps"));
const VectorMaps = async(() => import("./pages/maps/VectorMaps"));
*/

const masterComponentsList = [
  {
    name: "Dashboard",
    componentgroupid: 1,
  },
  {
    name: "OnRounds",
    componentgroupid: 3,
  },
  {
    name: "Products",
    componentgroupid: 7,
  },
  {
    name: "Library",
    componentgroupid: 12,
  },
  {
    name: "System",
    componentgroupid: 6,
  },
  {
    name: "Authorization",
    componentgroupid: 8,
  },
  {
    name: "Testing",
    componentgroupid: 9,
  },
  {
    name: "UI Elements",
    componentgroupid: 10,
  },
];
async function loadMasterComponents(masterComponentsList) {
  const masterComponentsPromises = masterComponentsList.map(async (component) => {
    const response = await fetchApi("components", "getComponents", {
      componentgroupid: component.componentgroupid,
      parentid: 0,
      order: "ORDERID ASC",
    });
    //console.log("routes response", response);

    const components = response.rows.map((component) => {
      return {
        name: component.SHORTNAME,
        importpath: component.IMPORTPATH,
        path: component.PATH,
      };
    });
    //console.log("components", components);

    const componentimports = components.reduce((acc, component) => {
      acc[component.name] = () => import(`${component.importpath}`);
      return acc;
    }, {});
    //console.log("componentimports", componentimports);

    const componentsloaded = components.map((component) => {
      return {
        name: component.name,
        component: lazy(componentimports[component.name]),
        path: component.path,
      };
    });
    //console.log("componentsloaded", componentsloaded);

    return {
      name: component.name,
      componentgroupid: component.componentgroupid,
      components: componentsloaded,
    };
  });
  return Promise.all(masterComponentsPromises);
}

let masterComponents = [];
try {
  masterComponents = await loadMasterComponents(masterComponentsList);
} catch (err) {
  console.error("Failed to load master components — rendering static routes only", err);
}
//console.log("masterComponents", masterComponents);

const displayRoutes = masterComponents.map((component) => {
  return {
    name: component.name,
    children: component.components.map((child) => {
      return {
        path: child.path,
        element: <Suspense fallback={<div>Loading...</div>}>{React.createElement(child.component)}</Suspense>,
      };
    }),
  };
});
//console.log("displayRoutes", displayRoutes);

function getRoutesByName(name) {
  const route = displayRoutes.find((item) => item.name === name);
  return route ? route.children : null;
}

const routes = [
  {
    path: "/",
    element: <PresentationLayout />,
    children: [
      {
        path: "",
        element: <Landing />,
      },
      {
        path: "/features",
        element: <PresentationFeatures />,
      },
      {
        path: "/pricing",
        element: <PresentationPricing />,
      },
      {
        path: "/about",
        element: <PresentationAbout />,
      },
      {
        path: "/contact",
        element: <PresentationContact />,
      },
      {
        path: "/contact",
        element: <PresentationContact />,
      },
      {
        path: "/contact",
        element: <PresentationContact />,
      },
      {
        path: "/cart",
        element: <Cart />,
      },
    ],
  },
  {
    path: "dashboard",
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: getRoutesByName("Dashboard"),
  },
  {
    path: "products",
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: getRoutesByName("Products"),
  },
  {
    path: "or",
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: getRoutesByName("OnRounds"),
  },
  {
    path: "ai",
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: getRoutesByName("Testing"),
  },
  {
    path: "library",
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: getRoutesByName("Library"),
  },
  {
    path: "system",
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: getRoutesByName("System"),
  },
  {
    path: "components",
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: getRoutesByName("UI Elements"),
  },
  {
    path: "documentation",
    element: <DocLayout />,
    children: [
      {
        path: "welcome",
        element: <Welcome />,
      },
      {
        path: "getting-started",
        element: <GettingStarted />,
      },
      {
        path: "policies-and-procedures",
        element: <PoliciesAndProcedures />,
      },
      {
        path: "policies-and-procedures/notice-of-privacy-practices",
        element: <PoliciesAndProcedures_NoticeOfPrivacyPractices />,
      },
      {
        path: "policies-and-procedures/notice-of-patient-rights-policy",
        element: <PoliciesAndProcedures_NoticeOfPatientRightsPolicy />,
      },
      {
        path: "policies-and-procedures/use-and-disclosure-of-phi",
        element: <PoliciesAndProcedures_UseAndDisclosureOfPHI />,
      },
      {
        path: "policies-and-procedures/minimal-necessary-use-and-disclosure",
        element: <PoliciesAndProcedures_MinimumNecessaryUseAndDisclosure />,
      },
      {
        path: "policies-and-procedures/auth-requirements",
        element: <PoliciesAndProcedures_AuthorizationRequirements />,
      },
      {
        path: "policies-and-procedures/auth-requirements/authorization-forms",
        element: <PoliciesAndProcedures_AuthorizationRequirements_AuthorizationForm />,
      },
      {
        path: "policies-and-procedures/auth-requirements/authorization-exceptions",
        element: <PoliciesAndProcedures_AuthorizationRequirements_AuthorizationExceptions />,
      },
      {
        path: "policies-and-procedures/auth-requirements/authorization-revocation",
        element: <PoliciesAndProcedures_AuthorizationRequirements_AuthorizationRevocation />,
      },
      {
        path: "policies-and-procedures/business-associate-agreements",
        element: <PoliciesAndProcedures_BusinessAssociateAgreements />,
      },
      {
        path: "policies-and-procedures/privacy-training-and-awareness",
        element: <PoliciesAndProcedures_PrivacyTrainingAndAwareness />,
      },
      {
        path: "policies-and-procedures/privacy-complaints-process",
        element: <PoliciesAndProcedures_PrivacyComplaintsProcess />,
      },
      {
        path: "policies-and-procedures/private-breach-notification",
        element: <PoliciesAndProcedures_PrivacyBreachNotification />,
      },
      {
        path: "policies-and-procedures/sanction-policy-for-privacy-violations",
        telement: <PoliciesAndProcedures_SanctionPolicyForPrivacyViolations />,
      },
      {
        path: "policies-and-procedures/privacy-monitoring-and-auditing",
        element: <PoliciesAndProcedures_PrivacyMonitoringAndAuditing />,
      },
      {
        path: "technical/overview",
        element: <TechnicalSafeguards />,
      },
      {
        path: "physical/overview",
        element: <PhysicalSafeguards />,
      },
      {
        path: "privacy/overview",
        element: <PrivacyCompliance />,
      },
      {
        path: "breach-notification/overview",
        element: <BreachNotification />,
      },
      {
        path: "record-keeping/overview",
        element: <RecordKeeping />,
      },
      {
        path: "incident-response/overview",
        element: <IncidentResponse />,
      },
      {
        path: "routing",
        element: <Routing />,
      },
      {
        path: "auth/auth0",
        element: <Auth0 />,
      },
      {
        path: "auth/cognito",
        element: <Cognito />,
      },
      {
        path: "auth/firebase",
        element: <Firebase />,
      },
      {
        path: "auth/jwt",
        element: <JWT />,
      },
      {
        path: "guards",
        element: <Guards />,
      },
      {
        path: "environment-variables",
        element: <EnvironmentVariables />,
      },
      {
        path: "deployment",
        element: <Deployment />,
      },
      {
        path: "theming",
        element: <Theming />,
      },
      {
        path: "api-calls",
        element: <APICalls />,
      },
      {
        path: "redux",
        element: <Redux />,
      },
      {
        path: "internationalization",
        element: <Internationalization />,
      },
      {
        path: "eslint-and-prettier",
        element: <ESLintAndPrettier />,
      },
      {
        path: "support",
        element: <Support />,
      },
    ],
  },
  {
    path: "auth",
    element: <AuthLayout />,
    children: getRoutesByName("Authorization"),
  },
  {
    path: "*",
    element: <AuthLayout />,
    children: [
      {
        path: "*",
        element: <Page404 />,
      },
    ],
  },
];
/*
  {
    path: "pages",
    element: <DashboardLayout />,
    children: [
      {
        path: "profile",
        element: (
          <AuthGuard>
            <Profile />
          </AuthGuard>
        ),
      },
      {
        path: "settings",
        element: <Settings />,
      },
      {
        path: "pricing",
        element: <Pricing />,
      },
      {
        path: "chat",
        element: <Chat />,
      },
      {
        path: "blank",
        element: <Blank />,
      },
      {
        path: "api-examples",
        element: <APIExamples />,
      },
    ],
  },
  {
    path: "projects",
    element: <DashboardLayout />,
    children: [
      {
        path: "",
        element: <Projects />,
      },
    ],
  },
  {
    path: "invoices",
    element: <DashboardLayout />,
    children: [
      {
        path: "",
        element: <InvoiceList />,
      },
      {
        path: "detail",
        element: <InvoiceDetails />,
      },
    ],
  },
  {
    path: "orders",
    element: <DashboardLayout />,
    children: [
      {
        path: "",
        element: <Orders />,
      },
    ],
  },
  {
    path: "tasks",
    element: <DashboardLayout />,
    children: [
      {
        path: "",
        element: <Tasks />,
      },
    ],
  },
  {
    path: "calendar",
    element: <DashboardLayout />,
    children: [
      {
        path: "",
        element: <Calendar />,
      },
    ],
  },
  {
    path: "forms",
    element: <DashboardLayout />,
    children: [
      {
        path: "pickers",
        element: <Pickers />,
      },
      {
        path: "selection-controls",
        element: <SelectionCtrls />,
      },
      {
        path: "selects",
        element: <Selects />,
      },
      {
        path: "text-fields",
        element: <TextFields />,
      },
      {
        path: "editors",
        element: <Editors />,
      },
      {
        path: "formik",
        element: <Formik />,
      },
    ],
  },
  {
    path: "tables",
    element: <DashboardLayout />,
    children: [
      {
        path: "simple-table",
        element: <SimpleTable />,
      },
      {
        path: "advanced-table",
        element: <AdvancedTable />,
      },
      {
        path: "data-grid",
        element: <DataGrid />,
      },
    ],
  },
  {
    path: "icons",
    element: <DashboardLayout />,
    children: [
      {
        path: "material-icons",
        element: <MaterialIcons />,
      },
      {
        path: "feather-icons",
        element: <FeatherIcons />,
      },
    ],
  },
  {
    path: "charts",
    element: <DashboardLayout />,
    children: [
      {
        path: "chartjs",
        element: <Chartjs />,
      },
      {
        path: "apexcharts",
        element: <ApexCharts />,
      },
    ],
  },
  {
    path: "maps",
    element: <DashboardLayout />,
    children: [
      {
        path: "google-maps",
        element: <GoogleMaps />,
      },
      {
        path: "vector-maps",
        element: <VectorMaps />,
      },
    ],
  },
  {
    path: "documentation",
    element: <DocLayout />,
    children: [
      {
        path: "welcome",
        element: <Welcome />,
      },
      {
        path: "getting-started",
        element: <GettingStarted />,
      },
      {
        path: "routing",
        element: <Routing />,
      },
      {
        path: "auth/auth0",
        element: <Auth0 />,
      },
      {
        path: "auth/cognito",
        element: <Cognito />,
      },
      {
        path: "auth/firebase",
        element: <Firebase />,
      },
      {
        path: "auth/jwt",
        element: <JWT />,
      },
      {
        path: "guards",
        element: <Guards />,
      },
      {
        path: "environment-variables",
        element: <EnvironmentVariables />,
      },
      {
        path: "deployment",
        element: <Deployment />,
      },
      {
        path: "theming",
        element: <Theming />,
      },
      {
        path: "api-calls",
        element: <APICalls />,
      },
      {
        path: "redux",
        element: <Redux />,
      },
      {
        path: "internationalization",
        element: <Internationalization />,
      },
      {
        path: "eslint-and-prettier",
        element: <ESLintAndPrettier />,
      },
      {
        path: "support",
        element: <Support />,
      },
    ],
  },
  {
    path: "changelog",
    element: <DocLayout />,
    children: [
      {
        path: "",
        element: <Changelog />,
      },
    ],
  },
  {
    path: "private",
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      {
        path: "",
        element: <ProtectedPage />,
      },
    ],
  },
];
*/
export default routes;
